<template>
	<div class="pairing-screen">
		
		<div v-if="!isSuccess" class="pairing-content accent-container">
			<img class="logo logo-pairing" src="/images/Buzztime_tv_white.svg" alt="Buzztime TV">
			
			<div class="pairing-content-inner" :class="{ 'layout-swapped': isSwapped, 'layout-animating': isAnimating }">
				<div class="left-panel">
					<h1 class="title">Pair this Display</h1>
					<p class="subtitle">To get started, pair this display to your account</p>
					
					<div class="pairing-options">
						<p class="scan-text">
							Open your camera and scan the QR code
						</p>
						
						<div class="option-divider">
							<span>OR</span>
						</div>
						
						<ol class="instructions-list">
							<li>Head to <strong>remote.buzztime.com</strong></li>
							<li>Log in with your Buzztime account</li>
							<li>Select the <span class="btn-plus">+</span> button</li>
							<li>Enter the 6-digit pairing code</li>
						</ol>
					</div>
				</div>
				
				<div class="right-panel">
					<div class="qr-section">
						<div class="qr-box">
							<qrcode-vue
									class="responsive-qr"
									:class="{ 'ghost-qr' : isLoading }"
									:value=" isLoading ? 'buzztime-tv-loading' : qrCodeUrl"
									render-as="svg"
									:margin="3"
									level="M"
									background="#ededed"
									foreground="#121212"
							/>
						</div>
					</div>
					
					<div class="right-panel-bottom">
						<div class="code-section">
							<span class="section-label">Pairing Code</span>
							<!-- TODO: Add refresh button to request new code. 60s time limit -->
							<div class="code-box mono">
								<div class="code-wrapper">
								<span class="code" :style="{ opacity: isLoading ? 0 : 1 }"
									  v-html="isLoading ? '&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;' : pairingCode">
                            	</span>
									<div v-if="isLoading" class="loading-bar-overlay">
										<div class="dynamic-bar"></div>
									</div>
								</div>
							</div>
						</div>
						
						<div class="timer-section">
							<span class="timer-label">Code will refresh in:</span>
							<span class="timer-value mono">{{ formattedTime }}</span>
						</div>
					</div>
				</div>
			</div>
			
		</div>
		
		<transition name="fade">
			<div v-if="isSuccess" class="success-content accent-container">
				<img class="logo logo-success" src="/images/Buzztime_tv_white.svg" alt="Buzztime TV">
				<div class="success-loading-bar">
					<div class="dynamic-bar"></div>
				</div>
				<h2 class="success-title">Connected to <span class="title-name">{{ venueName }}</span></h2>
				<p class="success-subtitle">Getting things ready...</p>
			</div>
		</transition>
	
	</div>
</template>

<script setup>
import {ref, computed, onMounted, onUnmounted} from 'vue';
import {getOrCreateDeviceId, setPairingToken} from '../services/virtualDisplay.js';
import QrcodeVue from 'qrcode.vue';
import {REMOTE_WS_BASE_URL} from "@/constants/api.js";

const emit = defineEmits(['pairing-complete']);

const pairingCode = ref('');
const timeRemaining = ref(0);
const layoutSwapIntervalMS = 30000;
const pairingRequestTimeoutMS = 15000;
const pairingRetryDelayMS = 5000;
let targetExpirationTime = 0;
const isLoading = ref(true);
const isSuccess = ref(false);
const venueName = ref('');
const isSwapped = ref(false);
const isAnimating = ref(false);

let timerInterval = null;
let wsCleanup = null;
let layoutInterval = null;
let retryTimeout = null;
const deviceId = getOrCreateDeviceId();

const formattedTime = computed(() => {
	if (isLoading.value || !timeRemaining.value || isNaN(timeRemaining.value)) {
		return '00:00';
	}
	const m = Math.floor(timeRemaining.value / 60).toString().padStart(2, '0');
	const s = (timeRemaining.value % 60).toString().padStart(2, '0');
	return `${m}:${s}`;
});

const generateCode = async () => {
	isLoading.value = true;
	const controller = new AbortController();
	const timeout = setTimeout(() => controller.abort(), pairingRequestTimeoutMS);
	try {
		// const response = await mockVirtualBackend.requestPairingCode(deviceId);
		const token = import.meta.env.VITE_JWT_AUTH_TOKEN;
		const url = import.meta.env.VITE_CHEERIOS_V2_URL
		const response = await fetch(`${url}/buzztime-tv/pairing/request`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${token}`
			},
			body: JSON.stringify({
				device_id: deviceId
			}),
			signal: controller.signal
		})
		
		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const data = await response.json();
		
		pairingCode.value = data.code;
		targetExpirationTime = new Date(data.ttl).getTime();
		
		updateTimeRemaining();
		startTimer();
	} catch (error) {
		console.error("Failed to fetch pairing code:", error);
		// startTimer only reschedules on success, so a failure would otherwise never retry
		clearTimeout(retryTimeout);
		retryTimeout = setTimeout(generateCode, pairingRetryDelayMS);
	} finally {
		clearTimeout(timeout);
		isLoading.value = false;
	}
};

const qrCodeUrl = computed(() => {
	const baseUrl = import.meta.env.VITE_REMOTE_PAIR_URL;
	return pairingCode.value ? `${baseUrl}?code=${pairingCode.value}` : '';
});

const updateTimeRemaining = () => {
	const now = Date.now();
	// Calculate seconds remaining, floor it, and ensure it never drops below 0
	const diffInSeconds = Math.max(0, Math.floor((targetExpirationTime - now) / 1000));
	timeRemaining.value = diffInSeconds;
	return diffInSeconds;
};

const startTimer = () => {
	if (timerInterval) clearInterval(timerInterval);
	
	timerInterval = setInterval(() => {
		const remaining = updateTimeRemaining();
		
		if (remaining <= 0) {
			// Code expired, fetch a fresh one automatically
			clearInterval(timerInterval);
			generateCode();
		}
	}, 1000);
};

const handlePairingSuccess = (data) => {
	// Stop timers and listeners
	if (timerInterval) clearInterval(timerInterval);
	if (wsCleanup) wsCleanup();
	
	// Save token
	setPairingToken(data.auth_token); // TODO: is this the right token to keep in LS? or the actual 6-digit token?
	venueName.value = data.site_name;
	isSuccess.value = true;
	
	// Show success screen transiently (3 seconds)
	// TODO: do we want to add actual functionality here?
	setTimeout(() => {
		// Emit back up to App to remount LoadingScreen or proceed to Home
		emit('pairing-complete', data);
	}, 3000);
};

const toggleLayout = () => {
	if (isAnimating.value) return; // Prevent spamming
	isAnimating.value = true;
	
	setTimeout(() => {
		isSwapped.value = !isSwapped.value;
		isAnimating.value = false;
	}, 300);
};

const setupWebSocket = () => {
	const wsBase = import.meta.env.VITE_REMOTE_WS_URL;
	if (!wsBase) {
		console.error("VITE_API_WS_URL is not defined in environment variables.");
		return;
	}
	
	const wsUrl = `${REMOTE_WS_BASE_URL}/socket/pairing?device_id=${deviceId}`;
	
	const ws = new WebSocket(wsUrl);
	
	ws.onopen = () => {
		console.log("WebSocket pairing channel connected.");
	};
	
	ws.onmessage = (event) => {
		try {
			const message = JSON.parse(event.data);
			
			if (message.type === 'pairing_validation' && message.success === true) {
				handlePairingSuccess(message);
			}
		} catch (error) {
			console.error("Failed to parse incoming WebSocket message:", error);
		}
	};
	
	ws.onerror = (error) => {
		console.error("WebSocket transport error occurred:", error);
	};
	
	ws.onclose = (event) => {
		console.log(`WebSocket disconnected. Code: ${event.code}`);
	};
	
	wsCleanup = () => {
		if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
			ws.close();
		}
	};
};

onMounted(() => {
	generateCode();
	layoutInterval = setInterval(() => {
		toggleLayout();
	}, layoutSwapIntervalMS);
	
	setupWebSocket()
});

onUnmounted(() => {
	if (timerInterval) clearInterval(timerInterval);
	if (wsCleanup) wsCleanup();
	if (layoutInterval) clearInterval(layoutInterval);
	if (retryTimeout) clearTimeout(retryTimeout);
});
</script>

<style scoped>
.pairing-screen {
	position: absolute;
	top: 0;
	left: 0;
	width: calc(var(--cqw) * 100);
	height: calc(var(--cqh) * 100);
	display: flex;
	justify-content: center;
	align-items: center;
	color: var(--color-text);
	z-index: 200;
}

.pairing-content {
	position: relative;
	display: flex;
	flex-direction: row;
	width: 80%;
	overflow: hidden;
	transition: opacity 0.3s ease, transform 0.3s ease;
	opacity: 1;
	transform: scale(1);
	border-radius: var(--border-radius-xl);
	padding-top: 6rem;
	/*margin-top: var(--spacing-2xl);*/
}

.pairing-content-inner {
	display: flex;
	flex-direction: row;
	width: 100%;
	transition: opacity 0.3s ease, transform 0.3s ease;
	opacity: 1;
	transform: scale(1);
	padding-bottom: var(--spacing-md);
}

.pairing-content-inner.layout-animating {
	opacity: 0;
	transform: scale(0.98);
}

.pairing-content-inner.layout-swapped {
	flex-direction: row-reverse;
}

/* Left Panel */
.left-panel {
	flex: 1;
	padding: var(--spacing-xl);
	display: flex;
	flex-direction: column;
	justify-content: center;
	text-align: center;
	
}

.logo {
	height: calc(var(--cqh) * 10);
	width: auto;
	margin-bottom: var(--spacing-md);
	opacity: 0.9;
}

.logo-pairing {
	position: absolute;
	top: var(--spacing-md);
	left: var(--spacing-md);
	align-self: flex-start;
}

.logo-success {
	width: auto;
	height: calc(var(--cqw) * 8);
}

.title {
	font-size: var(--font-size-title);
	font-weight: var(--font-weight-medium);
	margin-bottom: var(--spacing-sm);
}

.subtitle {
	font-size: var(--font-size-reg);
	color: var(--color-text-secondary);
	margin-bottom: var(--spacing-xl);
}

/* Pairing Options Layout */
.pairing-options {
	display: flex;
	flex-direction: column;
}

.scan-text {
	font-size: var(--font-size-md);
	text-align: center;
}

/* Divider line with "OR" in the middle */
.option-divider {
	display: flex;
	align-items: center;
	text-align: center;
	color: var(--color-text-secondary);
	font-size: var(--font-size-md);
	font-weight: var(--font-weight-semibold);
	margin: var(--spacing-xl) 0;
}

.option-divider::before,
.option-divider::after {
	content: '';
	flex: 1;
	border-bottom: var(--border-thin) var(--color-border);
}

.option-divider span {
	padding: 0 var(--spacing-md);
}

/* Numbered List */
.instructions-list {
	font-size: var(--font-size-md);
	color: var(--color-text);
	padding-left: var(--spacing-xl);
	text-align: start;
	line-height: 1.8;
	margin: 0 0 0 var(--spacing-xl);
}

.instructions-list li {
	margin-bottom: var(--spacing-sm);
}

.instructions-list strong {
	color: var(--color-text);
}

.btn-plus {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	background-color: #8F8E8E51;
	color: #ededed;
	width: 2.5rem;
	height: 2.5rem;
	border-radius: var(--border-radius-md);
	border: var(--border-thin) var(--color-border);
	font-weight: var(--font-weight-light);
	font: var(--font-family-mono);
	margin: 0 var(--spacing-xs);
	font-size: 2.5rem;
	line-height: 1;
	vertical-align: middle;
}

/* Right Panel */
.right-panel {
	flex: 1;
	padding: var(--spacing-xl);
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: var(--spacing-2xl);
	justify-content: center;
	border-left: var(--border-thin) var(--color-border);
}

/* QR Code Area */
.qr-box {
	width: calc(var(--cqw) * 20);
	height: calc(var(--cqw) * 20);
	min-width: 150px;
	min-height: 150px;
	max-width: 400px;
	max-height: 400px;
	border-radius: var(--border-radius-xl);
	overflow: hidden;
}

.responsive-qr {
	width: 100%;
	height: 100%;
	display: block;
}

.ghost-qr {
	opacity: 0.15;
	animation: pulse-ghost 1.5s ease-in-out infinite;
}

@keyframes pulse-ghost {
	0%, 100% {
		opacity: 0.1;
	}
	50% {
		opacity: 0.25;
	}
}

/* Pairing Code Area */
.code-section {
	display: flex;
	flex-direction: column;
	align-items: center;
	margin-bottom: var(--spacing-md);
	width: 100%;
}

.section-label {
	font-size: var(--font-size-md);
	color: var(--color-text-secondary);
	margin-bottom: var(--spacing-sm);
	text-transform: uppercase;
	letter-spacing: 0.1em;
}

.code-box {
	background: var(--gradient-primary);
	padding: 2px;
	border-radius: var(--border-radius-md);
}

.code {
	display: block;
	font-size: var(--font-size-title);
	font-weight: var(--font-weight-medium);
	text-align: center;
	letter-spacing: 0.25em;
	color: var(--color-text);
	transition: opacity 0.3s ease;
}

.code-wrapper {
	position: relative;
	display: flex;
	justify-content: center;
	align-items: center;
	background-color: var(--color-popup-dark);
	border-radius: calc(var(--border-radius-md) - 2px);
	padding: var(--spacing-md) var(--spacing-xl);
	padding-right: calc(var(--spacing-lg) - 0.25em);
	
}

.loading-bar-overlay {
	position: absolute;
	left: var(--spacing-xl);
	right: var(--spacing-xl);
	top: 0;
	bottom: 0;
	display: flex;
	align-items: center;
}

.dynamic-bar {
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	background: var(--gradient-primary);
	box-shadow: 0 0 20px var(--color-primary-light);
	animation: stretch-bounce 1s infinite;
}

@keyframes stretch-bounce {
	0%, 5% {
		left: 0%;
		width: 1.8rem;
		height: 1.8rem;
		border-radius: 50%;
		animation-timing-function: ease-in;
	}
	25% {
		left: 25%;
		width: 30%;
		height: 0.35rem;
		border-radius: 1rem;
		animation-timing-function: ease-out;
	}
	45%, 55% {
		left: calc(100% - 1.8rem);
		width: 1.8rem;
		height: 1.8rem;
		border-radius: 50%;
		animation-timing-function: ease-in;
	}
	75% {
		left: 25%;
		width: 30%;
		height: 0.35rem;
		border-radius: 1rem;
		animation-timing-function: ease-out;
	}
	95%, 100% {
		left: 0%;
		width: 1.8rem;
		height: 1.8rem;
		border-radius: 50%;
	}
}

/* Timer Area */
.timer-section {
	display: flex;
	width: 100%;
	flex-direction: row;
	align-items: center;
	justify-content: center;
}

.timer-label {
	font-size: var(--font-size-sm);
	color: var(--color-text-secondary);
	margin-right: var(--spacing-sm);
}

.timer-value {
	font-size: var(--font-size-lg);
}

/* Success State */
.success-content {
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	max-width: calc(var(--cqw) * 75);
	padding: var(--spacing-xl);
	border-radius: var(--border-radius-xl);
}

.success-title {
	font-size: var(--font-size-title);
	font-weight: var(--font-weight-light);
	margin-top: var(--spacing-xl);
	margin-bottom: var(--spacing-sm);
}

.title-name {
	font-weight: var(--font-weight-semibold);
}

.success-subtitle {
	font-size: var(--font-size-lg);
	color: var(--color-text-secondary);
}

.success-loading-bar {
	position: relative;
	width: calc(var(--cqw) * 33);
	margin-top: var(--spacing-md);
}

.success-loading-bar .dynamic-bar {
	animation: stretch-bounce 1.5s infinite;
}

.layout-swapped .right-panel {
	border-left: none;
}

/* When reversed, the left panel moves to the right, so give it the center border */
.layout-swapped .left-panel {
	border-left: var(--border-thin, 1px solid) var(--color-border);
}

/* Vue Transitions */
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}
</style>